import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';

import { DATE_FORMAT } from './constants/date.constants';
import { IFullName } from '../portal/candidates/interfaces';
import { IListNavigator, IPage, IPager } from './interfaces';
import { IPaginationOptions } from './pagination/interfaces';
import { IProgress } from 'projects/labcorp/clinical-trials-ui/src/lib/interfaces';
import { US_LOCALE } from './constants/locale.constants';

const datePipe = new DatePipe(US_LOCALE);

export function clone<T>(object: T): T {
  return JSON.parse(JSON.stringify(object));
}

export const dynamicPaginate = (totalItems: number, currentPage: number, perPage: number, maxPages: number): IPager => {
  // calculate total pages
  const totalPages = Math.ceil(totalItems / perPage);

  let cp = currentPage;

  // ensure current page isn't out of range
  if (currentPage < 1) {
    cp = 1;
  }
  else if (currentPage > totalPages) {
    cp = totalPages;
  }

  let startPage: number;
  let endPage: number;

  if (totalPages <= maxPages) {
    // total pages less than max so show all pages
    startPage = 1;
    endPage = totalPages;
  }
  else {
    // total pages more than max so calculate start and end pages
    const maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
    const maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;

    if (cp <= maxPagesBeforeCurrentPage) {
      // current page near the start
      startPage = 1;
      endPage = maxPages;
    }
    else if (cp + maxPagesAfterCurrentPage >= totalPages) {
      // current page near the end
      startPage = totalPages - maxPages + 1;
      endPage = totalPages;
    }
    else {
      // current page somewhere in the middle
      startPage = cp - maxPagesBeforeCurrentPage;
      endPage = cp + maxPagesAfterCurrentPage;
    }
  }

  // Map an array of all possible page numbers/offsets.
  // Necessary to navigate to pages beyond the visible range by index
  const allPages: IPage[] = [...Array(totalPages).keys()].map(i => {
    return {
      number: i + 1,
      offset: i * perPage
    };
  });

  // Slice out the array of currently visible pages
  const pages = allPages.slice(startPage - 1, endPage);

  // return object with all pager properties required by the view
  return {
    allPages,
    currentPage: cp,
    currentPageIndex: cp - 1,
    endPage,
    pages,
    perPage,
    startPage,
    totalItems,
    totalPages,
  };
};

export function formatDateString(dateString: string): string {
  return datePipe.transform(dateString, DATE_FORMAT) || 'N/A';
}

export function getAddress(addresses: fhir4.Address[]): string {
  let address = 'N/A';
  const addressData = addresses?.[0];
  if (addressData) {
    address = addressData.text ? addressData.text : `${addressData.line }, ${ addressData.city }, ${ addressData.state } ${ addressData.postalCode }`;
  }

  return address;
}

export function getCandidateStatus(progress: IProgress[]): string | null {
  const currentProgress = progress ?  progress.find(progressState => !progressState.endDate) : null;

  return currentProgress ? currentProgress.subjectState.coding[0].code : null;
}

export function getDateAdded(progress: IProgress[]): string {
  const candidateState = progress ?  progress.find(progressState => progressState.subjectState?.coding[0].code === 'candidate') : null;
  if (candidateState && candidateState.startDate) {
    return datePipe.transform(candidateState.startDate, DATE_FORMAT);
  }

  return 'N/A';
}

export function getEmail(telecom: fhir4.ContactPoint[]): string {
  return telecom?.find(obj => obj.system === 'email')?.value || 'N/A';
}

export function getEthnicity(extensions: fhir4.Extension[]): string {
  const ethnicityData = extensions?.find(extension => extension.url.includes('us-core-ethnicity')) || null;

  return ethnicityData?.extension?.find( ethnicity => ethnicity.url === 'text').valueString || 'N/A';
}

export function getName(name: fhir4.HumanName[]): IFullName {
  const nameData = name ? name.find(obj => obj.use === 'official') : null;

  return nameData ? {
    firstName: nameData.given[0],
    lastName: nameData.family
  } : null;
}

export function getPhoneNumber(contactPoints: fhir4.ContactPoint[], use: string): string {
  const numberData = contactPoints ? contactPoints.find(contactPoint => (contactPoint.system === 'phone' && contactPoint.use === use)) : null;
  let phoneNumber = 'N/A';

  if (numberData) {
    const numberString = numberData.value.replace(/[ ]|[(]|[)]|[-]/gm, '');

    phoneNumber = `${ numberString.substr(0, 3) }-${ numberString.substr(3, 3) }-${ numberString.substr(6, 4) }`;
  }

  return phoneNumber;
}

export function getRace(extensions: fhir4.Extension[]): string {
  const raceData = extensions?.find(extension => extension.url.includes('us-core-race')) || null;

  return raceData?.extension?.find( race => race.url === 'text').valueString || 'N/A';
}

export const objectToHttpParams = <T extends object>(query: T): HttpParams => {
  let params = new HttpParams();

  if (query) {
    Object.keys(query).forEach(key => params = params.append(key, query[key]));
  }

  return params;
};

export const sessionStorageSupported = () => typeof(sessionStorage) !== 'undefined';

export const updateListNavigator = (listNavigator: IListNavigator, paginationOptions: IPaginationOptions, total: number): IListNavigator => {
  const newPager = dynamicPaginate(total, listNavigator?.pager?.currentPage || 1, paginationOptions.perPage, paginationOptions.maxPages);

  return  {
    pager: newPager,
    paginationData: {
      maxPages: paginationOptions.maxPages,
      count: paginationOptions.perPage,
      total,
    },
  };
};
